import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Wallet,
  TrendingUp,
  Timer,
  ShieldCheck,
  ArrowDownToLine,
  ArrowUpToLine,
  Gift,
  Copy,
  CreditCard,
  Coins,
  Zap,
  ArrowRight,
  Home,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { walletAPI, exchangeAPI, transactionAPI, kycAPI, referralAPI, safecoinAPI, gamefuelAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [rates, setRates] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [kyc, setKyc] = useState(null);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [safecoinBalance, setSafecoinBalance] = useState(0);
  const [gamefuelCredit, setGamefuelCredit] = useState(0);

  const loadData = useCallback(async () => {
    try {
      const [walletRes, ratesRes, txRes, kycRes, refRes, scRes, gfRes] = await Promise.all([
        walletAPI.getWallet(),
        exchangeAPI.getRates(),
        transactionAPI.getTransactions({ limit: 5 }),
        kycAPI.getStatus(),
        referralAPI.getInfo(),
        safecoinAPI.getBalance().catch(() => ({ data: { balance: 0 } })),
        gamefuelAPI.getStatus().catch(() => ({ data: { active_credit: 0 } })),
      ]);
      setWallet(walletRes.data);
      setRates(ratesRes.data);
      setTransactions(txRes.data.transactions);
      setKyc(kycRes.data);
      setReferral(refRes.data);
      setSafecoinBalance(scRes.data?.balance || 0);
      setGamefuelCredit(gfRes.data?.active_credit || 0);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const formatUSD = (val) => {
    if (val === undefined || val === null) return '$0.00';
    return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const totalPortfolio = wallet
    ? wallet.usdt_balance + (wallet.trx_balance * (rates?.rate_lock?.trx_usd_rate || 0.25))
    : 0;

  const rateLockExpiry = rates?.rate_lock?.expires_at
    ? new Date(rates.rate_lock.expires_at)
    : null;

  const hoursLeft = rateLockExpiry
    ? Math.max(0, Math.round((rateLockExpiry - new Date()) / (1000 * 60 * 60)))
    : 0;

  const kycProgress = kyc?.status === 'approved' ? 100 : kyc?.status === 'pending' ? 50 : 0;

  const txTypeLabel = (type) => {
    const map = {
      bonus: t('dashboard.bonus'),
      deposit: t('dashboard.depositTx'),
      withdraw: t('dashboard.withdrawTx'),
      trade: t('dashboard.tradeTx'),
      fee: t('dashboard.feeTx'),
      referral_bonus_inviter: t('dashboard.referralTx'),
      referral_bonus_invited: t('dashboard.inviteBonus'),
    };
    return map[type] || type;
  };

  if (loading) {
    return (
      <DashboardLayout title={t('dashboard.title')}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('dashboard.title')}>
      <div data-testid="patient-dashboard" className="space-y-6">
        {/* Welcome Banner */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold font-['Space_Grotesk']">
              {t('dashboard.welcome')}, {user?.name || 'User'}
            </h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('dashboard.welcomeDesc')}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
            className="border-[hsl(var(--border))] hover:bg-white/5 gap-1.5 text-xs"
          >
            <Home className="w-3.5 h-3.5" /> {t('nav.home')}
          </Button>
        </div>

        {/* Top Row - Portfolio + Balances */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {/* Total Portfolio */}
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] xl:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('dashboard.totalPortfolio')}</span>
                </div>
                <Badge className="bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20 hover:bg-[rgba(45,211,111,0.18)]">
                  {t('common.active')}
                </Badge>
              </div>
              <div data-testid="dashboard-portfolio-value" className="text-3xl font-semibold font-['Space_Grotesk'] tabular-nums">
                {formatUSD(totalPortfolio)}
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
                {t('dashboard.lastUpdate')}: {rates?.last_updated ? new Date(rates.last_updated).toLocaleTimeString(lang) : '-'}
              </p>
            </CardContent>
          </Card>

          {/* USDT Balance */}
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-4">
                <Wallet className="w-4 h-4" />
                <span className="text-sm font-medium">{t('dashboard.usdtBalance')}</span>
              </div>
              <div data-testid="dashboard-usdt-balance" className="text-2xl font-semibold font-['Space_Grotesk'] tabular-nums">
                {formatUSD(wallet?.usdt_balance)}
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('dashboard.tether')}</p>
            </CardContent>
          </Card>

          {/* TRX Balance */}
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-4">
                <Wallet className="w-4 h-4" />
                <span className="text-sm font-medium">{t('dashboard.trxBalance')}</span>
              </div>
              <div data-testid="dashboard-trx-balance" className="text-2xl font-semibold font-['Space_Grotesk'] tabular-nums">
                {wallet?.trx_balance?.toFixed(2) || '0.00'} TRX
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                ~{formatUSD(wallet?.trx_balance * (rates?.rate_lock?.trx_usd_rate || 0.25))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Rate Lock */}
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
                  <Timer className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('dashboard.rateLock')}</span>
                </div>
                <Badge className="bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20 hover:bg-[rgba(45,211,111,0.18)]">
                  {hoursLeft}{t('dashboard.hoursLeft')}
                </Badge>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">USDT/TRY</span>
                  <span className="font-semibold tabular-nums">{rates?.rate_lock?.locked_rate?.toFixed(2) || '-'} TL</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">TRX/USD</span>
                  <span className="font-semibold tabular-nums">${rates?.rate_lock?.trx_usd_rate?.toFixed(4) || '-'}</span>
                </div>
                <Progress value={(hoursLeft / 24) * 100} className="h-1.5" />
              </div>
            </CardContent>
          </Card>

          {/* KYC Status */}
          <Card data-testid="dashboard-kyc-card" className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('dashboard.kycStatus')}</span>
                </div>
                <Badge className={
                  kyc?.status === 'approved'
                    ? 'bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20 hover:bg-[rgba(45,211,111,0.18)]'
                    : kyc?.status === 'pending'
                      ? 'bg-[rgba(247,200,75,0.12)] text-[#F7C84B] border-[#F7C84B]/20 hover:bg-[rgba(247,200,75,0.18)]'
                      : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]/80'
                }>
                  {kyc?.status === 'approved' ? t('dashboard.kycApproved') : kyc?.status === 'pending' ? t('dashboard.kycPending') : t('dashboard.kycRequired')}
                </Badge>
              </div>
              <Progress value={kycProgress} className="h-1.5 mb-3" />
              <Button
                size="sm"
                variant={kyc?.status === 'approved' ? 'ghost' : 'default'}
                onClick={() => navigate('/kyc')}
                className={kyc?.status === 'approved' ? '' : 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'}
              >
                {kyc?.status === 'approved' ? t('common.details') : kyc?.status === 'pending' ? t('dashboard.kycViewStatus') : t('dashboard.kycStart')}
              </Button>
            </CardContent>
          </Card>

          {/* Referral */}
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
                  <Gift className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('dashboard.referral')}</span>
                </div>
                <Badge className="bg-[rgba(247,200,75,0.12)] text-[#F7C84B] border-[#F7C84B]/20 text-xs hover:bg-[rgba(247,200,75,0.18)]">
                  $50 {t('dashboard.perInvite')}
                </Badge>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <code className="font-mono text-sm bg-[hsl(var(--secondary))] px-3 py-1.5 rounded-lg">
                  {referral?.referral_code || user?.referral_code || '-'}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="referral-copy-button"
                  onClick={() => {
                    navigator.clipboard.writeText(referral?.referral_code || user?.referral_code || '');
                    toast.success(t('dashboard.referralCopied'));
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">{t('dashboard.invited')}</span>
                  <span className="font-semibold">{referral?.referred_count || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">{t('dashboard.totalEarnings')}</span>
                  <span className="font-semibold text-[#2DD36F]">${referral?.total_earnings?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions - Enhanced */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Button
            data-testid="dashboard-deposit-button"
            onClick={() => navigate('/wallet')}
            className="h-14 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 flex-col gap-1"
          >
            <ArrowDownToLine className="w-5 h-5" />
            <span className="text-xs">{t('dashboard.deposit')}</span>
          </Button>
          <Button
            data-testid="dashboard-withdraw-button"
            onClick={() => navigate('/wallet')}
            variant="outline"
            className="h-14 border-[hsl(var(--border))] hover:bg-white/5 flex-col gap-1"
          >
            <ArrowUpToLine className="w-5 h-5" />
            <span className="text-xs">{t('dashboard.withdraw')}</span>
          </Button>
          <Button
            onClick={() => navigate('/exchange')}
            variant="outline"
            className="h-14 border-[hsl(var(--border))] hover:bg-white/5 flex-col gap-1"
          >
            <TrendingUp className="w-5 h-5" />
            <span className="text-xs">{t('dashboard.buySell')}</span>
          </Button>
          <Button
            onClick={() => navigate('/cards')}
            variant="outline"
            className="h-14 border-[hsl(var(--border))] hover:bg-white/5 flex-col gap-1"
          >
            <CreditCard className="w-5 h-5" />
            <span className="text-xs">{t('cards.nav')}</span>
          </Button>
        </div>

        {/* SafeCoin & GameFuel Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] cursor-pointer hover:border-[hsl(var(--primary))]/30 transition-colors" onClick={() => navigate('/safecoin')}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[rgba(55,240,196,0.08)] flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 text-[hsl(var(--primary))]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[hsl(var(--muted-foreground))]">SafeCoin</p>
                <p className="text-lg font-semibold font-mono">{safecoinBalance} SC</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            </CardContent>
          </Card>
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] cursor-pointer hover:border-[#F7931A]/30 transition-colors" onClick={() => navigate('/wallet')}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[rgba(247,147,26,0.08)] flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#F7931A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[hsl(var(--muted-foreground))]">GameFuel</p>
                <p className="text-lg font-semibold font-mono">${gamefuelCredit.toFixed(2)}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="text-lg font-['Space_Grotesk']">{t('dashboard.recentTransactions')}</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                <p>{t('dashboard.noTransactions')}</p>
                <Button size="sm" className="mt-3 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" onClick={() => navigate('/exchange')}>{t('dashboard.firstTransaction')}</Button>
              </div>
            ) : (
              <Table data-testid="dashboard-transactions-table">
                <TableHeader>
                  <TableRow className="border-[hsl(var(--border))]">
                    <TableHead>{t('dashboard.txType')}</TableHead>
                    <TableHead>{t('dashboard.txAsset')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.txAmount')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.txDate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id} className="border-[hsl(var(--border))]">
                      <TableCell>
                        <Badge className={
                          tx.entry_type === 'bonus' ? 'bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20 hover:bg-[rgba(45,211,111,0.18)]'
                          : tx.entry_type === 'deposit' ? 'bg-[rgba(18,200,178,0.12)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 hover:bg-[rgba(18,200,178,0.18)]'
                          : tx.entry_type === 'withdraw' ? 'bg-[rgba(217,74,74,0.12)] text-[#D94A4A] border-[#D94A4A]/20 hover:bg-[rgba(217,74,74,0.18)]'
                          : tx.entry_type === 'trade' ? 'bg-[rgba(23,185,230,0.12)] text-[hsl(var(--accent))] border-[hsl(var(--accent))]/20 hover:bg-[rgba(23,185,230,0.18)]'
                          : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]/80'
                        }>
                          {txTypeLabel(tx.entry_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="uppercase font-medium">{tx.asset}</TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${tx.amount >= 0 ? 'text-[#2DD36F]' : 'text-[#D94A4A]'}`}>
                        {tx.amount >= 0 ? '+' : ''}{tx.amount?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-[hsl(var(--muted-foreground))] text-sm">
                        {new Date(tx.created_at).toLocaleDateString(lang)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
